/**
 * Compact pill rendering the upload `upload_source` enum — used across
 * the RuntimeFsBrowser file list, the viewer-pane attribution panel,
 * and the ExecutionLedger upload-row + detail body.
 *
 * Six canonical sources plus a fallback for forward-compat (the
 * lexruntime stamper might introduce a new source before the lexportal
 * SPA redeploys).
 *
 * Canonical enum reference:
 *   ai-docs/upload-attribution-and-delete-permissions.md §2.3
 */

import {
  Archive,
  Bot,
  Code,
  HelpCircle,
  MessageCircle,
  Monitor,
  Send,
  type LucideIcon,
} from "lucide-react";

import type { UploadSource } from "@/types/execution-ledger";

interface SourceMeta {
  icon: LucideIcon;
  label: string;
  tooltip: string;
  /** Tailwind chip color — uses the dual-stack `bg-…/text-…` pair so
   *  the chip looks distinct from the kind/status pills nearby. */
  tone: string;
}

const SOURCE_META: Record<UploadSource, SourceMeta> = {
  lexide: {
    icon: Monitor,
    label: "LexIDE",
    tooltip: "Uploaded via LexIDE",
    tone: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  },
  whatsapp: {
    icon: MessageCircle,
    label: "WhatsApp",
    tooltip: "Uploaded via WhatsApp channel",
    tone: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  telegram: {
    icon: Send,
    label: "Telegram",
    tooltip: "Uploaded via Telegram channel",
    tone: "bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200",
  },
  agent: {
    icon: Bot,
    label: "Agent",
    tooltip: "Generated by a Lexiun Agent",
    tone: "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200",
  },
  api: {
    icon: Code,
    label: "API",
    tooltip: "Uploaded via the platform API",
    tone: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  },
  migration: {
    icon: Archive,
    label: "Backfill",
    tooltip: "Backfilled by the upload-attribution migration",
    tone: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  },
};

const UNKNOWN_META = (raw: string): SourceMeta => ({
  icon: HelpCircle,
  label: raw || "unknown",
  tooltip: "Unknown upload source",
  tone: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
});

interface Props {
  source: UploadSource | string | undefined;
  /** Compact variant (icon only, label kept as `aria-label` + tooltip)
   *  — used on dense file-tree rows where horizontal space is scarce. */
  compact?: boolean;
}

export function SourceChip({ source, compact }: Props) {
  const meta =
    source && source in SOURCE_META
      ? SOURCE_META[source as UploadSource]
      : UNKNOWN_META(source ?? "");
  const Icon = meta.icon;
  if (compact) {
    return (
      <span
        title={meta.tooltip}
        aria-label={meta.label}
        className={`inline-flex h-5 w-5 items-center justify-center rounded ${meta.tone}`}
        data-testid={`source-chip-compact-${source ?? "unknown"}`}
      >
        <Icon className="h-3 w-3" />
      </span>
    );
  }
  return (
    <span
      title={meta.tooltip}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${meta.tone}`}
      data-testid={`source-chip-${source ?? "unknown"}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

/** Six canonical sources surfaced for the multi-select filter chip
 *  group. Iteration order is the operator-facing display order. */
export const UPLOAD_SOURCES: readonly UploadSource[] = [
  "lexide",
  "whatsapp",
  "telegram",
  "agent",
  "api",
  "migration",
] as const;

/** Operator-facing label resolver (mirrors `SOURCE_META[s].label`).
 *  Exposed for filter UIs that show the label without rendering the chip. */
export function uploadSourceLabel(s: UploadSource | string): string {
  return (SOURCE_META[s as UploadSource] ?? UNKNOWN_META(s)).label;
}
